/** Cottage compositions share wall openings, roof bearings and usable floor
 * bounds. The garden/entry stay authored features of each arrangement. */
import type {AssetVec3} from '../proceduralAssets.js';
import type {FrontierBuilder} from './frontierExtras.js';
import type {FrontierPlacePlan} from './frontierTown.js';
import {buildFacade,type FacadePlan,type FacadeOpening} from './facade.js';
import {counterStand,counterTop} from './counterContact.js';
import * as pmath from '../pmath.js';
export interface MerchantCottage {
  version:1; entrance:AssetVec3; facades:FacadePlan[];
  destinations:{id:'living'|'storage'|'shop';position:AssetVec3}[];
  restockRoute:AssetVec3[]; stockFacing:AssetVec3;
}
export function frontierHome(b:FrontierBuilder,d:FrontierPlacePlan['design']):MerchantCottage {
  const {node,box,floor,post,roof,barrel,slot,awning,part}=b;
  const w=d.homeWidth,half=w/2,split=d.homeForm!=='veranda',notched=d.homeForm==='wing';
  const wingW=2.6,join=half-wingW,mainW=split?w-wingW:w,mainX=split?-wingW/2:0;
  const wingX=half-wingW/2,wingH=notched?3.05:3.7,doorX=d.homeForm==='veranda'?-.65:0,doorW=1.8;
  const wall=(width:number,height:number,x:number,z:number)=>box([width,height,.25],[x,.3+height/2,z],0,true);
  // The notched cottage has an actual L-shaped floor and enclosure.
  if(notched){floor(mainW+.3,6.5,mainX-.15);floor(wingW+.3,3.5,wingX+.15,1.5);}
  else floor(w+.6,6.5);
  part('home:floorboards',()=>{
    const boards=(width:number,depth:number,x:number,z:number)=>{
      const count=Math.ceil(width/.45),pitch=width/count;
      const plank=node('box',[pitch-.014,.012,depth],[x-width/2+pitch/2,.302,z],12);
      plank.modifiers=[{type:'array',mode:'linear',count,offset:[pitch,0,0]}];
    };
    if(notched){boards(mainW-.3,5.65,mainX,0);boards(wingW-.3,2.65,wingX,1.5);}else boards(w-.3,5.65,0,0);
  });
  const shopX=-half+1.35,shopW=1.75,facades:FacadePlan[]=[];
  const facade=(id:string,center:AssetVec3,axis:'x'|'z',outward:1|-1,width:number,height:number,openings:FacadeOpening[])=>{
    const f:FacadePlan={id:`home:${id}`,center,axis,outward,width,height,thickness:.3,openings};facades.push(f);buildFacade(b,f);
  };
  const frontCenter=notched?mainX:0;
  const street:FacadeOpening[]=[{id:'entrance',u:doorX-frontCenter,bottom:.3,width:doorW,height:2.65,family:'door'},
    {id:'shop',u:shopX-frontCenter,bottom:1.15,width:shopW,height:1.5,family:'shop'}];
  if(!notched)street.push({id:'window',u:wingX,bottom:1.65,width:1,height:1.25,family:'casement'});
  facade('street',[frontCenter,.3,3],'x',1,notched?mainW:w,3.7,street);
  facade('garden',[-half+.12,.3,0],'z',-1,6,3.7,[{id:'window',u:.4,bottom:1.45,width:1.35,height:1.5,family:d.homeForm==='veranda'?'shuttered':d.homeForm==='wing'?'transom':'casement'}]);
  facade('rear',[notched?mainX:0,.3,-3],'x',-1,notched?mainW:w,3.7,[{id:'bedroom',u:-half+1.5-(notched?mainX:0),bottom:1.5,width:1.1,height:1.3,family:'shuttered'}]);
  box([.3,wingH,notched?3:6],[half-.12,.3+wingH/2,notched?1.5:0],0,true);
  if(notched){
    facade('wing',[wingX,.3,3],'x',1,wingW,wingH,[{id:'window',u:0,bottom:1.55,width:1,height:1.1,family:'casement'}]);
    wall(wingW,wingH,wingX,0);
    box([.25,3.7,3],[join,2.15,-1.5],0,true);
    box([.25,.65,3],[join,3.675,1.5],0,true);
  }
  // Gable infill is derived from each roof's own bearing and pitch.
  const gable=(width:number,depth:number,roofDepth:number,eave:number,x:number,z:number,pitch:number,turn=false,wallTop=4)=>{
    const slope=pmath.sin(pitch)/pmath.cos(pitch),rise=depth/2*slope;
    const base=eave+(roofDepth-depth)/2*slope-.09/pmath.cos(pitch),cap=Math.max(.02,base-wallTop);
    for(const side of [-1,1]){
      const px=turn?x:x+side*(width/2-.12),pz=turn?z+side*(width/2-.12):z;
      box(turn?[depth,cap,.25]:[.25,cap,depth],[px,wallTop+cap/2,pz],0);
      const n=node('box',turn?[depth,rise,.25]:[.25,rise,depth],[px,base+rise/2,pz],0);n.modifiers=[{type:'taper',axis:'y',factor:.01}];
    }
  };
  roof(mainW+.8,7.1,4.1,mainX,0,d.roofPitch);gable(mainW,6,7.1,4.1,mainX,0,d.roofPitch);
  if(split){
    if(notched){roof(wingW+.7,3.6,3.4,wingX,1.5,.4);gable(wingW,3,3.6,3.4,wingX,1.5,.4,false,3.35);}
    else {roof(6.8,wingW+.7,4.1,wingX,0,.42,1);gable(6,wingW,wingW+.7,4.1,wingX,0,.42,true);}
  }
  part('home:corners',()=>{
    for(const x of [-half+.1,half-.1]){
      box([.2,(x>0?wingH:3.7)+.1,.24],[x,.35+(x>0?wingH:3.7)/2,3.18],1);
      const stones=node('box',[.36,.22,.35],[x,.45,3.1],4);
      stones.modifiers=[{type:'array',mode:'linear',count:4,offset:[0,.32,0]}];
    }
  });
  // The recess is framed by a supported porch; a wide veranda is a different
  // outdoor room, not a scaled copy of the small entrance canopy.
  const porchLeft=-half+.45,porchRight=notched?join-.15:half-.45,porchW=porchRight-porchLeft,porchX=(porchRight+porchLeft)/2,backY=3.45;
  floor(porchW+.3,2.5,porchX,4.13);awning('porch',porchX,porchW,3.08,5.18,backY,backY-.315,'wall');
  floor(2,1,doorX,5.5,.12);
  const counterCenter:AssetVec3=[shopX,1.23,3.12],counterSize:AssetVec3=[shopW+.25,.16,.75];
  const serving=counterStand(counterCenter,counterSize,-1);
  part('home:shop',()=>{
    // Counter sits through the hatch. Its outer ledge is sheltered by cloth;
    // the merchant works from the interior, with a clear route behind it.
    counterTop(b,counterCenter,counterSize,1,-1);
    for(const x of [shopX-shopW/2+.1,shopX+shopW/2-.1])box([.14,.9,.14],[x,.76,3.37],1,true);
    for(let i=0;i<3;i++){
      box([.44,.09,.42],[shopX+(i-1)*.52,1.35,3.16],3);
      const goods=node('sphere',[.12],[shopX+(i-1)*.52,1.5,3.18],i===1?8:9);
      goods.modifiers=[{type:'array',mode:'linear',count:2,offset:[0,0,-.2]}];
    }
    box([shopW,.48,.09],[shopX,2.98,3.21],5);
    for(const x of [shopX-.6,shopX+.6])box([.06,.2,.13],[x,2.72,3.15],7);
  });
  // A partial-height partition encloses the living corner while keeping a
  // full-height 1.8 m opening on the central circulation line.
  part('home:living',()=>{
    const span=half-.95;
    box([span,2.4,.16],[-half+span/2,1.5,-.55],0,true);
    box([span,.15,.24],[-half+span/2,2.73,-.55],1);
    box([1.5,.5,1.95],[-half+1.4,.55,-1.85],1,true);
    box([1.4,.17,1.85],[-half+1.4,.88,-1.85],6);
    box([1.4,.055,1],[-half+1.4,.98,-1.45],5);
    box([.9,.16,.4],[-half+1.4,1.02,-2.45],6);
  });
  const hearthX=notched?wingX:half-1.2,hearthZ=notched?.65:-2.3;
  part('home:hearth',()=>{
    box([1.3,.9,.8],[hearthX,.75,hearthZ],4,true);box([.7,.3,.1],[hearthX,.6,hearthZ+.45],11);
    const chimneyTop=notched?5.2:4.1+3.55*pmath.sin(d.roofPitch)/pmath.cos(d.roofPitch)+1;
    box([.65,chimneyTop-3,.7],[hearthX,(chimneyTop+3)/2,hearthZ],4);box([.84,.2,.9],[hearthX,chimneyTop,hearthZ],4);
  });
  part('home:packing-table',()=>{
    box([1.25,.12,.65],[shopX,1.02,.05],12,true);
    for(const x of [shopX-.45,shopX+.45])box([.14,.65,.48],[x,.625,.05],1,true);
    node('cylinder',[.12,.15,.24],[shopX-.3,1.2,.05],4);
    box([.4,.07,.32],[shopX+.25,1.12,.08],6);
  });
  part('home:entry-lantern',()=>{
    const x=doorX+.97;
    box([.09,.1,.44],[x,2.65,3.28],7);
    node('cylinder',[.1,.1,.28],[x,2.45,3.46],11);
    box([.28,.08,.28],[x,2.61,3.46],7);
  });
  part('home:storage',()=>{
    const x=half-.48;
    for(const z of [1.1,2.5])box([.12,2.3,.12],[x,1.45,z],1,true);
    const shelves=node('box',[.65,.12,1.7],[x,.65,1.8],1);
    shelves.modifiers=[{type:'array',mode:'linear',count:3,offset:[0,.7,0]}];
    box([.1,2.3,1.7],[x+.28,1.45,1.8],1,true);
    for(const z of [1.25,1.8,2.35])node('cylinder',[.17,.2,.35],[x,1.58,z],4);
  });
  const signX=porchX-porchW/2-.28;box([.1,.1,1],[signX,2.85,3.5],1);box([.12,.55,.6],[signX,2.5,3.8],5);
  barrel(half-.5,4.35,0);
  const gardenX=-half-1.6;
  for(let z=0;z<3;z++)for(let x=0;x<3;x++)node('sphere',[.27],[gardenX-.7+x*.7,.3,1.8+z*.75],8);
  for(const z of [1.3,4.2]){for(const x of [gardenX-1.4,gardenX+1.4])post(x,z,.8,0);box([3,.1,.12],[gardenX,.65,z],1);}
  slot('garden','garden',[gardenX,0,4.85],[gardenX,0,6.3],Math.PI);
  slot('shopkeeper','serve',[...serving],[shopX,.3,1.1],0);
  slot('shop-customer','browse',[shopX,.3,4.25],[shopX,.3,4.7],Math.PI);
  const storage:AssetVec3=[half-1.3,.3,1.8];
  return {version:1,entrance:[doorX,0,6],facades,stockFacing:[1,0,0],
    destinations:[{id:'living',position:[0,.3,-1.8]},{id:'storage',position:storage},{id:'shop',position:[...serving]}],
    restockRoute:[[...serving],[shopX,.3,1.65],[0,.3,1.65],[storage[0],.3,1.65], [...storage]]};
}
